// ============================================================================
// ENUMS & CONSTANTS
// ============================================================================

export const CONTINENTS = [
  "asia",
  "africa",
  "south-america",
  "north-america",
  "oceania",
  "europe",
] as const;
export type Continent = (typeof CONTINENTS)[number];

export const DIFFICULTY_LEVELS = ["easy", "medium", "hard"] as const;
export type DifficultyLevel = (typeof DIFFICULTY_LEVELS)[number];

export const TRIP_TYPES = ["guided", "private", "resort"] as const;
export type TripType = (typeof TRIP_TYPES)[number];

export const TRIP_STATUSES = ["draft", "published", "archived"] as const;
export type TripStatus = (typeof TRIP_STATUSES)[number];

export const TRIP_DATE_STATUSES = [
  "open",
  "few_spots",
  "full",
  "guaranteed",
  "cancelled",
] as const;
export type TripDateStatus = (typeof TRIP_DATE_STATUSES)[number];

export const ORDER_STATUSES = [
  "inquiry",
  "quoted",
  "confirmed",
  "deposit_paid",
  "fully_paid",
  "completed",
  "cancelled",
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const USER_ROLES = ["admin", "editor", "marketing", "readonly"] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const COMMUNICATION_TYPES = ["email", "phone", "note", "sms", "status_change", "payment"] as const;
export type CommunicationType = (typeof COMMUNICATION_TYPES)[number];

export const COMMUNICATION_DIRECTIONS = ["inbound", "outbound", "internal"] as const;
export type CommunicationDirection = (typeof COMMUNICATION_DIRECTIONS)[number];

export const VOUCHER_STATUSES = ["active", "used", "expired"] as const;
export type VoucherStatus = (typeof VOUCHER_STATUSES)[number];

export const NEWSLETTER_STATUSES = [
  "draft",
  "scheduled",
  "sending",
  "sent",
] as const;
export type NewsletterStatus = (typeof NEWSLETTER_STATUSES)[number];

// ============================================================================
// DESTINATION
// ============================================================================

export interface Destination {
  id: string;
  slug: string;
  name: string;
  nameSk?: string;
  continent: Continent;
  shortDescription?: string;
  shortDescriptionSk?: string;
  description?: string;
  descriptionSk?: string;
  bestTimeToVisit?: string;
  climateInfo?: string;
  practicalInfo?: {
    visa?: string;
    currency?: string;
    language?: string;
    timezone?: string;
    electricity?: string;
    vaccination?: string;
  };
  highlights?: Array<{
    title: string;
    description: string;
    image?: string;
  }>;
  regions?: Array<{
    name: string;
    description: string;
    image?: string;
  }>;
  cuisine?: string;
  cuisineSk?: string;
  activities?: string[];
  whyWithUs?: string[];
  featuredImage?: string;
  galleryImages?: string[];
  heroVideoUrl?: string;
  parentId?: string;
  guideId?: string;
  metaTitle?: string;
  metaDescription?: string;
  sortOrder: number;
  isFeatured: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// GUIDE
// ============================================================================

export interface Guide {
  id: string;
  slug: string;
  name: string;
  photo?: string;
  bio?: string;
  bioSk?: string;
  livingIn?: string;
  yearsExperience?: number;
  specializations?: string[];
  youtubeChannelUrl?: string;
  featuredVideos?: string[];
  email?: string;
  phone?: string;
  whatsapp?: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// TRIP
// ============================================================================

export interface Trip {
  id: string;
  slug: string;
  title: string;
  titleSk?: string;
  shortDescription?: string;
  shortDescriptionSk?: string;
  description?: string;
  descriptionSk?: string;
  priceFrom: number;
  priceOriginal?: number;
  priceEur?: number;
  durationDays: number;
  difficultyLevel?: DifficultyLevel;
  groupSizeMin: number;
  groupSizeMax: number;
  tripType: TripType;
  guideId?: string;
  destinationId?: string;
  featuredImage?: string;
  galleryImages?: string[];
  youtubeVideos?: string[];
  guideVideoUrl?: string;
  itinerary?: ItineraryDay[];
  included?: string[];
  excluded?: string[];
  isFeatured: boolean;
  isLastMinute: boolean;
  hasCzechGuide: boolean;
  allowsOnlineBooking: boolean;
  tags?: string[];
  metaTitle?: string;
  metaDescription?: string;
  status: TripStatus;
  createdAt: Date;
  updatedAt: Date;
}

export interface ItineraryDay {
  day: number;
  title: string;
  description?: string;
  image?: string;
  accommodation?: string;
  meals?: {
    breakfast: boolean;
    lunch: boolean;
    dinner: boolean;
  };
}

// ============================================================================
// TRIP DATE
// ============================================================================

export interface TripDate {
  id: string;
  tripId: string;
  startDate: Date;
  endDate: Date;
  price: number;
  priceEur?: number;
  priceSingleRoom?: number;
  capacity: number;
  bookedCount: number;
  guideId?: string;
  status: TripDateStatus;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// CUSTOMER
// ============================================================================

export interface Customer {
  id: string;
  email: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  street?: string;
  city?: string;
  zip?: string;
  country: string;
  birthDate?: Date;
  passportNumber?: string;
  passportExpiry?: Date;
  nationality?: string;
  totalSpent: number;
  tripsCount: number;
  firstTripDate?: Date;
  lastTripDate?: Date;
  score: number;
  newsletterSubscribed: boolean;
  newsletterPreferences?: string[];
  notes?: string;
  source?: string;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// ORDER
// ============================================================================

export interface Order {
  id: string;
  orderNumber: string;
  customerId: string;
  tripId: string;
  tripDateId?: string;
  status: OrderStatus;
  participants: Participant[];
  adultsCount: number;
  childrenCount: number;
  totalPrice: number;
  currency: string;
  depositAmount?: number;
  depositDueDate?: Date;
  depositPaidAt?: Date;
  fullPaymentDue?: Date;
  fullPaymentPaidAt?: Date;
  stripePaymentIntentId?: string;
  stripeCustomerId?: string;
  contractUrl?: string;
  invoiceUrl?: string;
  source: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  assignedTo?: string;
  customerNotes?: string;
  internalNotes?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface Participant {
  name: string;
  birthDate?: string;
  passport?: string;
  passportExpiry?: string;
  nationality?: string;
}

// ============================================================================
// COMMUNICATION
// ============================================================================

export interface Communication {
  id: string;
  orderId?: string;
  customerId: string;
  type: CommunicationType;
  direction: CommunicationDirection;
  subject?: string;
  content?: string;
  emailMessageId?: string;
  emailStatus?: string;
  attachments?: string[];
  createdBy?: string;
  createdAt: Date;
}

// ============================================================================
// USER (Admin)
// ============================================================================

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  isActive: boolean;
  lastLoginAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// REVIEW
// ============================================================================

export interface Review {
  id: string;
  tripId?: string;
  destinationId?: string;
  guideId?: string;
  customerName: string;
  customerLocation?: string;
  rating: number;
  title?: string;
  content: string;
  tripDate?: Date;
  isFeatured: boolean;
  isVideo: boolean;
  videoUrl?: string;
  isApproved: boolean;
  createdAt: Date;
}

// ============================================================================
// NEWSLETTER
// ============================================================================

export interface NewsletterSubscriber {
  id: string;
  email: string;
  preferences?: string[];
  isActive: boolean;
  confirmedAt?: Date;
  confirmationToken?: string;
  source?: string;
  unsubscribedAt?: Date;
  unsubscribeReason?: string;
  createdAt: Date;
}

export interface NewsletterCampaign {
  id: string;
  subject: string;
  subjectSk?: string;
  content: string;
  contentSk?: string;
  aiGenerated: boolean;
  aiPrompt?: string;
  sentCount: number;
  openedCount: number;
  clickedCount: number;
  status: NewsletterStatus;
  scheduledAt?: Date;
  sentAt?: Date;
  createdBy?: string;
  createdAt: Date;
}

// ============================================================================
// GIFT VOUCHER
// ============================================================================

export interface GiftVoucher {
  id: string;
  code: string;
  amount: number;
  currency: string;
  buyerName: string;
  buyerEmail: string;
  recipientName?: string;
  recipientEmail?: string;
  personalMessage?: string;
  designTemplate: string;
  status: VoucherStatus;
  usedAt?: Date;
  usedOrderId?: string;
  expiresAt: Date;
  pdfUrl?: string;
  sentAt?: Date;
  createdAt: Date;
}

// ============================================================================
// QUIZ
// ============================================================================

export interface QuizResult {
  id: string;
  answers: Record<string, string>;
  recommendedDestinations: string[];
  email?: string;
  subscribedNewsletter: boolean;
  createdAt: Date;
}

// ============================================================================
// PAGE VIEW (Analytics)
// ============================================================================

export interface PageView {
  id: string;
  visitorId: string;
  pageType: string;
  pageId?: string;
  createdAt: Date;
}

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

export interface ApiResponse<T> {
  data: T;
  success: true;
}

export interface ApiError {
  error: string;
  message: string;
  success: false;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  success: true;
}

// ============================================================================
// EXTENDED TYPES (with relations)
// ============================================================================

export interface TripWithRelations extends Trip {
  destination?: Destination;
  guide?: Guide;
  dates?: TripDate[];
  reviews?: Review[];
}

export interface DestinationWithRelations extends Destination {
  guide?: Guide;
  trips?: Trip[];
  parent?: Destination;
  children?: Destination[];
}

export interface GuideWithRelations extends Guide {
  destinations?: Destination[];
  trips?: Trip[];
}

export interface OrderWithRelations extends Order {
  customer?: Customer;
  trip?: Trip;
  tripDate?: TripDate;
  communications?: Communication[];
}
